<template>
  <SoContainer>
    <div class="d-flex">
      <h4 class="mr-2">Tariff Comparison Table</h4>
      <SoCopyToClipboard :element-to-copy="el" />
    </div>
    <SoRow>
      <SoCol>
        <SoWidget plain no-border no-padding>
          <div ref="tq-comp-table">
            <p class="mb-2">
              <template
                v-if="!tariffQuoter.isGSPSearch && tariffQuoter.address"
              >
                Address: {{ tariffQuoter.address.formatted }}
                {{ tariffQuoter.address.county }}
                {{ tariffQuoter.address.postcode }}
                <br />
              </template>
              <template v-else>
                GSP: {{ tariffQuoter.gsp }} ({{ tariffQuoter.gspName }})
                <br />
              </template>
              Date: {{ timestamp }} <br />{{ vatInclusiveNote }}
              <br />
              Please be aware that the tariff we offer is subject to change, and
              the quote I have provided today may not be available in the future
            </p>
            <table class="mb-2">
              <thead>
                <tr>
                  <th></th>
                  <th v-for="(bundle, index) in bundles" :key="index">
                    {{ bundle.name }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Type</td>
                  <td v-for="(bundle, index) in bundles" :key="index">
                    {{ bundle.type }}
                  </td>
                </tr>
                <tr>
                  <td>Duration</td>
                  <td v-for="(bundle, index) in bundles" :key="index">
                    {{ bundle.duration ? bundle.duration + " months" : "N/A" }}
                  </td>
                </tr>
                <tr v-if="hasMixedVatRates">
                  <td>VAT</td>
                  <td v-for="(rates, index) in vatRatesByBundle" :key="index">
                    <div v-if="rates.elec !== undefined">
                      Electricity: {{ vatRatePercentLabel(rates.elec) }}
                    </div>
                    <div v-if="rates.gas !== undefined">
                      Gas: {{ vatRatePercentLabel(rates.gas) }}
                    </div>
                    <template
                      v-if="rates.elec === undefined && rates.gas === undefined"
                    >
                      N/A
                    </template>
                  </td>
                </tr>
                <template v-if="!tariffQuoter.quoteForMeterpoints">
                  <ComparisonTableRow
                    v-if="tariffQuoter.isGasSelected"
                    name="Gas"
                    :bundles="bundles"
                    :usage="tariffQuoter.selectedUsage"
                  />
                  <ComparisonTableRow
                    v-if="tariffQuoter.isElectricitySelected"
                    name="Electricity 1-Rate"
                    :bundles="bundles"
                    :usage="tariffQuoter.selectedUsage"
                    is-electricity
                  />
                  <ComparisonTableRow
                    v-if="
                      tariffQuoter.isElectricitySelected &&
                      tariffQuoter.isTwoRateSelected &&
                      (tariffQuoter.selectedUsage.electricityDay ||
                        tariffQuoter.selectedUsage.electricityNight)
                    "
                    name="Electricity 2-Rate"
                    :bundles="bundles"
                    :usage="tariffQuoter.selectedUsage"
                    is-electricity
                    is-two-rate
                  />
                </template>
                <template v-else>
                  <ComparisonTableRow
                    v-for="mpxn in tariffQuoter.meterpointFuelUsage"
                    :key="mpxn.id"
                    :name="`${mpxn.isElectricity ? 'MPAN' : 'MPRN'} - ${
                      mpxn.id
                    }`"
                    :bundles="bundles"
                    :usage="mpxn.usage"
                    :is-electricity="mpxn.isElectricity"
                    :is-two-rate="mpxn.isTwoRate"
                    is-mpxn
                  />
                </template>
                <tr>
                  <td :colspan="bundles.length + 1"><b>Cost & Payments</b></td>
                </tr>
                <tr>
                  <td>Estimated Energy Costs</td>
                  <td v-for="(bundle, index) in bundles" :key="index">
                    {{
                      formatMoney({
                        amount: getAnnualCost(bundle),
                      })
                    }}
                  </td>
                </tr>
                <tr>
                  <td>Monthly Payment (Equal)</td>
                  <td v-for="(bundle, index) in bundles" :key="index">
                    {{
                      formatMoney({
                        amount: getAnnualCost(bundle) / 12,
                      })
                    }}
                  </td>
                </tr>
                <tr>
                  <td>Monthly Payment (Seasonal)</td>
                  <td v-for="(bundle, index) in bundles" :key="index">
                    {{
                      formatMoney({
                        amount: (getAnnualCost(bundle) / 12) * 0.75,
                      })
                    }}
                    Summer
                    <br />
                    {{
                      formatMoney({
                        amount: (getAnnualCost(bundle) / 12) * 1.25,
                      })
                    }}
                    Winter
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </SoWidget>
      </SoCol>
    </SoRow>
  </SoContainer>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useTariffQuoter } from "@/stores/tariffQuoter"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import { ITariffBundle } from "@/models/tariffBundle/tariffBundle.types"
  import ComparisonTableRow from "../components/ComparisonTableRow.vue"
  import SoCopyToClipboard from "@/components/SoCopyToClipboard.vue"
  import SoContainer from "@/components/SoContainer.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoCol from "@/components/SoCol.vue"
  import { vatRatePercentLabel } from "@/helpers/vatHelper"
  import dayjs from "dayjs"

  export default defineComponent({
    components: {
      ComparisonTableRow,
      SoCopyToClipboard,
      SoContainer,
      SoWidget,
      SoRow,
      SoCol,
    },
    data() {
      return {
        tariffQuoter: useTariffQuoter(),
        el: null as HTMLElement | null,
        formatMoney,
        vatRatePercentLabel,
      }
    },
    computed: {
      timestamp() {
        return dayjs().format("dddd, MMMM D, YYYY h:mm A")
      },
      bundles() {
        return this.tariffQuoter.filteredBundles
      },
      // Per-column rates, straight off the tariffs whose prices are displayed:
      // the view supplies the applied rate and the client-side strip overwrites
      // it with the rate it re-based to.
      vatRatesByBundle() {
        return this.bundles.map((bundle) => ({
          elec: (bundle.elec1Rate ?? bundle.elec2Rate)?.productDetails.vatRate,
          gas: bundle.gas?.productDetails.vatRate,
        }))
      },
      // Columns can carry different rates — products move to 0% individually, so
      // a mixed table is the expected state mid-cutover. A summary line can only
      // speak for the whole table when every column agrees.
      hasMixedVatRates() {
        const distinct = (rates: (number | undefined)[]) =>
          new Set(rates.filter((rate) => rate !== undefined)).size
        return (
          distinct(this.vatRatesByBundle.map((r) => r.elec)) > 1 ||
          distinct(this.vatRatesByBundle.map((r) => r.gas)) > 1
        )
      },
      vatInclusiveNote() {
        if (this.hasMixedVatRates) {
          return "VAT rates differ between the tariffs shown — see the VAT row below"
        }
        const rateOf = (key: "elec" | "gas") =>
          this.vatRatesByBundle
            .map((rates) => rates[key])
            .find((rate) => rate !== undefined)
        const elecRate = rateOf("elec")
        const gasRate = rateOf("gas")

        // Never generalise one fuel's rate to "All charges" — an unknown gas
        // rate must not be absorbed into a claim that gas is VAT-free.
        if (elecRate !== undefined && gasRate !== undefined) {
          return elecRate === gasRate
            ? `All charges shown are inclusive of ${vatRatePercentLabel(
                elecRate,
              )} VAT`
            : `Electricity charges are inclusive of ${vatRatePercentLabel(
                elecRate,
              )} VAT; gas charges are inclusive of ${vatRatePercentLabel(
                gasRate,
              )} VAT`
        }
        if (elecRate !== undefined) {
          return `Electricity charges are inclusive of ${vatRatePercentLabel(
            elecRate,
          )} VAT`
        }
        if (gasRate !== undefined) {
          return `Gas charges are inclusive of ${vatRatePercentLabel(
            gasRate,
          )} VAT`
        }
        // The quote must always carry a VAT statement, even when the data
        // exposes no rate to name.
        return "All prices are VAT inclusive."
      },
    },
    mounted() {
      this.el = this.$refs["tq-comp-table"] as HTMLElement
    },
    methods: {
      getAnnualCost(bundle: ITariffBundle) {
        return this.tariffQuoter.getAnnualCostForBundle(bundle)
      },
    },
  })
</script>

<style scoped>
  table,
  tr,
  td {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    border: 1px solid rgb(var(--v-theme-border));
    font-size: 16px;
    background-color: rgb(var(--v-theme-surface));
  }

  th,
  td {
    padding: 4px;
  }
</style>
